import React, { useState, useRef } from "react";

export default function BatchUploadBookletsForm({ client = null, url, rtq_id = null }) {
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [files, setFiles] = useState([]);

  const csrfToken = globalThis.document
    .querySelector('meta[name="csrf-token"]')
    .getAttribute("content");

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files).filter(
      (f) => f.type === "application/pdf"
    );
    setFiles((prev) => [...prev, ...droppedFiles]);
  };

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files).filter(
      (f) => f.type === "application/pdf"
    );
    setFiles((prev) => [...prev, ...selectedFiles]);
    // Reset the input so the same file can be selected again
    e.target.value = "";
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const filesPresent = files.length > 0;
  const blankClass = "border-dashed border-gray-300";
  const dragOverClass = "border-dashed border-blue-500 bg-blue-50";
  const dropZoneClass = isDragging ? `${blankClass} ${dragOverClass}` : blankClass;

  const handleSubmit = (e) => {
    if (files.length === 0) {
      e.preventDefault();
      return;
    }

    // Create a FormData object and append files
    const formData = new FormData(e.target);

    // Remove the empty file input and add our files
    formData.delete("files[]");
    files.forEach((file) => {
      formData.append("files[]", file);
    });

    // Submit via fetch to handle the file array properly
    e.preventDefault();

    fetch(url, {
      method: "POST",
      body: formData,
      headers: {
        "X-CSRF-Token": csrfToken,
      },
    }).then((response) => {
      if (response.redirected) {
        window.location.href = response.url;
      } else if (response.ok) {
        window.location.reload();
      }
    });
  };

  return (
    <form
      id="batch_upload_booklets"
      action={url}
      method="post"
      encType="multipart/form-data"
      className="w-full"
      onSubmit={handleSubmit}
    >
      <input
        type="hidden"
        name="authenticity_token"
        value={csrfToken}
        autoComplete="off"
      />
      {client && (
        <input
          type="hidden"
          name="client_id"
          value={client.id}
          autoComplete="off"
        />
      )}
      {rtq_id && (
        <input
          type="hidden"
          name="rtq_id"
          value={rtq_id}
          autoComplete="off"
        />
      )}
      <div className="space-y-6">
        <div>
          <label className="block text-gray-700 text-sm font-bold mb-2">
            Files
          </label>
          <div
            onClick={() => fileInputRef.current.click()}
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            className={`cursor-pointer border-2 rounded-lg p-6 text-center ${dropZoneClass}`}
          >
            <div className="text-gray-500">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                stroke="currentColor"
                fill="none"
                viewBox="0 0 48 48"
              >
                <path
                  d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <p className="mt-2">
                Drag PDF booklets here or click to select
              </p>
              <p className="mt-1 text-xs text-gray-400">
                You can select multiple files
              </p>
            </div>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              accept="application/pdf"
              multiple
              onChange={handleFileSelect}
            />
          </div>
        </div>

        {filesPresent && (
          <div>
            <label className="block text-gray-700 text-sm font-bold mb-2">
              Selected Files ({files.length})
            </label>
            <ul className="border rounded-lg divide-y divide-gray-200">
              {files.map((file, index) => (
                <li
                  key={index}
                  className="flex items-center justify-between px-4 py-3"
                >
                  <div className="flex items-center">
                    <svg
                      className="h-5 w-5 text-red-500 mr-3"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-sm text-gray-700">{file.name}</span>
                    <span className="ml-2 text-xs text-gray-400">
                      ({(file.size / 1024 / 1024).toFixed(2)} MB)
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeFile(index)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <svg
                      className="h-5 w-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div>
          <input
            type="submit"
            name="commit"
            value={`Upload ${files.length > 0 ? files.length + " booklet(s)" : ""} and Process`}
            className={
              "button button__primary" + (filesPresent ? "" : " button__disabled")
            }
            disabled={filesPresent ? null : "disabled"}
          />
        </div>
      </div>
    </form>
  );
}
